/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Grid, Card, CardContent, Stack, Chip, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import { STORAGE_KEYS } from '../../constants';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Helper to format numbers with commas (using 'en-IN' locale)
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value);

// Helper function to convert a "yyyy-MM" string into "MonthName yyyy"
const convertYearMonthToMonthName = (yearMonthStr) => {
  const [year, month] = yearMonthStr.split('-');
  const date = new Date(year, month - 1);
  const monthName = date.toLocaleString('default', { month: 'long' });
  return `${monthName} ${year}`;
};

// Helper to abbreviate large numbers for Y-axis ticks (e.g., 1500000 -> 1.5M)
const abbreviateNumber = (value) => {
  if (value >= 1e9) return (value / 1e9).toFixed(1) + 'B';
  if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
  if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
  return value;
};

const FeeGraphsComponent = () => {
  const theme = useTheme();
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classFilter, setClassFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Retrieve schoolId from localStorage (ensure it's already set)
  const schoolId = localStorage.getItem(STORAGE_KEYS.SCHOOL_ID);

  // Fetch aggregated fee data from the API using schoolId in the header
  const fetchGraphData = async () => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const response = await axiosInstance.get('/api/fees/graph', {
        headers: { schoolId },
        params: {
          classId: classFilter || undefined,
          sectionId: sectionFilter || undefined,
        },
      });
      setGraphData(response.data);
    } catch (err) {
      console.error('Error fetching fee graph data:', err);
      setError(getApiErrorMessage(err, 'Error fetching fee graph data.'));
      setGraphData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!schoolId) return;
    setLoadingClasses(true);
    axiosInstance
      .get('/api/classes', { headers: { schoolId } })
      .then((res) => setClasses(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        setClasses([]);
        setError(getApiErrorMessage(err, 'Failed to load classes.'));
      })
      .finally(() => setLoadingClasses(false));
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId || !classFilter) {
      setSections([]);
      return;
    }
    setLoadingSections(true);
    axiosInstance
      .get(`/api/sections/class/${classFilter}`, { headers: { schoolId } })
      .then((res) => setSections(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        setSections([]);
        setError(getApiErrorMessage(err, 'Failed to load sections.'));
      })
      .finally(() => setLoadingSections(false));
  }, [schoolId, classFilter]);

  // Prepare monthly data array from the monthlyCollected object,
  // converting keys to month names (e.g., "October 2024")
  const monthlyData =
    graphData && graphData.monthlyCollected
      ? Object.keys(graphData.monthlyCollected).map((monthKey) => ({
          month: convertYearMonthToMonthName(monthKey),
          amount: graphData.monthlyCollected[monthKey],
        }))
      : [];

  // Prepare data for the overall pie chart (collected vs. pending)
  const pieData = graphData
    ? [
        { name: 'Collected', value: graphData.collected },
        { name: 'Pending', value: graphData.pending },
      ]
    : [];

  // Define pie chart colors: green for Collected, red for Pending
  const pieColors = ['#4caf50', '#f44336'];

  const cardBase = {
    borderRadius: 6,
    border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
    boxShadow: 'none',
    backgroundColor: theme.palette.background.paper,
  };

  return (
    <Box p={0.8}>
      <Grid container spacing={1.4} alignItems="center" sx={{ mb: 1.6 }}>
        <Grid item xs={12} sm={5} md={4}>
          <FormControl fullWidth size="small" disabled={loading || loadingClasses}>
            <InputLabel id="fees-graph-class-label">Class</InputLabel>
            <Select
              labelId="fees-graph-class-label"
              value={classFilter}
              label="Class"
              onChange={(e) => {
                setClassFilter(e.target.value);
                setSectionFilter('');
              }}
            >
              <MenuItem value="">All Classes</MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls.classId} value={String(cls.classId)}>
                  {cls.className || 'Unnamed Class'}
                </MenuItem>
              ))}
              {!classes.length && !loadingClasses && (
                <MenuItem value="" disabled>
                  No classes available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={5} md={4}>
          <FormControl fullWidth size="small" disabled={loading || loadingSections || !classFilter}>
            <InputLabel id="fees-graph-section-label">Section</InputLabel>
            <Select
              labelId="fees-graph-section-label"
              value={sectionFilter}
              label="Section"
              onChange={(e) => setSectionFilter(e.target.value)}
            >
              <MenuItem value="">All Sections</MenuItem>
              {sections.map((sec) => (
                <MenuItem key={sec.sectionId} value={String(sec.sectionId)}>
                  {sec.sectionName || 'Unnamed Section'}
                </MenuItem>
              ))}
              {!classFilter && (
                <MenuItem value="" disabled>
                  Select class first
                </MenuItem>
              )}
              {classFilter && !sections.length && !loadingSections && (
                <MenuItem value="" disabled>
                  No sections available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2} md={2}>
          <Button
            variant="contained"
            fullWidth
            onClick={fetchGraphData}
            disabled={loading}
            sx={{ height: 40, textTransform: 'none', fontWeight: 700 }}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      {loading && (
        <Box display="flex" justifyContent="start" my={4}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && !graphData && !hasSearched && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Select filters and click Search to view analytics.
        </Typography>
      )}
      {!loading && !error && graphData && (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={{ ...cardBase, background: `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.02)})` }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Total Fee
                  </Typography>
                  <Typography variant="h5" fontWeight={800} mt={0.5}>
                    ₹ {formatCurrency(graphData.totalFee)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    All billed fees this academic year
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ ...cardBase, background: `linear-gradient(120deg, ${alpha(theme.palette.success.main, 0.12)}, ${alpha(theme.palette.success.main, 0.02)})` }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Collected
                  </Typography>
                  <Typography variant="h5" fontWeight={800} mt={0.5}>
                    ₹ {formatCurrency(graphData.collected)}
                  </Typography>
                  <Chip label="Healthy inflow" size="small" sx={{ mt: 1, bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.main, fontWeight: 700 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ ...cardBase, background: `linear-gradient(120deg, ${alpha(theme.palette.warning.main, 0.12)}, ${alpha(theme.palette.warning.main, 0.02)})` }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Pending
                  </Typography>
                  <Typography variant="h5" fontWeight={800} mt={0.5}>
                    ₹ {formatCurrency(graphData.pending)}
                  </Typography>
                  <Chip label="Needs follow-up" size="small" sx={{ mt: 1, bgcolor: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.main, fontWeight: 700 }} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {monthlyData.length > 0 && (
            <Card sx={cardBase}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Monthly Collections
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Collected fees by month
                    </Typography>
                  </Box>
                </Stack>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={monthlyData}
                    margin={{ top: 12, right: 12, left: 12, bottom: 12 }}
                  >
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={abbreviateNumber} />
                    <Tooltip formatter={(value) => `₹ ${formatCurrency(value)}`} />
                    <Legend />
                    <Bar
                      dataKey="amount"
                      fill={theme.palette.primary.main}
                      name="Collected"
                      radius={[10, 10, 0, 0]}
                      isAnimationActive={true}
                      animationDuration={1500}
                      animationEasing="ease-out"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {pieData.length > 0 && (
            <Card sx={cardBase}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Overall Distribution
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Collected vs pending split
                    </Typography>
                  </Box>
                </Stack>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={92}
                      innerRadius={52}
                      label
                      isAnimationActive={true}
                      animationDuration={1500}
                      animationEasing="ease-out"
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === 0 ? theme.palette.success.main : theme.palette.warning.main}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `₹ ${formatCurrency(value)}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default FeeGraphsComponent;
